import React, { useCallback, useEffect, useState } from 'react';
import { CustomTitle, ToggleSwitch } from '../../../components';
import { Flex, message } from 'antd';
import {
  GetAiSettings,
  UpdateAiControlsSettings,
} from '../../../apis/settings';
import { useToken } from '../../../common/helper';
import { useSpin } from '../../../components/SpinContext';
import { useUnAuthorize } from '../../../hooks';
import { AiSectionGroup, UpdateAiSettingsPayload } from './types';
import AiControlTable from './componentes/AiControlTable';
interface AIControlsProps {
  title?: string;
}

const AIControls: React.FC<AIControlsProps> = ({ title }) => {
  const { token, decodedUserToken } = useToken();
  const decodedToken = decodedUserToken();
  const { unauthorize } = useUnAuthorize();
  const [toggle, setToggle] = useState<boolean | null>(null);
  const [aiNumber, setAiNumber] = useState<{
    ai_transfer_number: string | null;
    ai_agent_number: string | null;
  }>({
    ai_transfer_number: null,
    ai_agent_number: null,
  });
  const [aiControlSettings, setAiControlSettings] = useState<AiSectionGroup[]>(
    []
  );
  const { showSpin, hideSpin } = useSpin();
  const refreshToken = localStorage.getItem('@refreshToken');

  interface LocalSettingsData {
    ai_enabl?: boolean | null;
    ai_transfer_number?: string | null;
    ai_agent_number?: string | null;
    ai_controls?: AiSectionGroup[];
  }

  const updateLocalStates = useCallback((data?: LocalSettingsData) => {
    if (data) {
      if (data?.ai_enabl !== undefined) {
        setToggle(data.ai_enabl);
      }
      if (data?.ai_controls) {
        setAiControlSettings(data?.ai_controls);
      }
      setAiNumber({
        ai_transfer_number: data?.ai_transfer_number,
        ai_agent_number: data?.ai_agent_number,
      });
    }
  }, []);

  const fetchAiSettings = useCallback(() => {
    if (!token) return;
    showSpin();
    return GetAiSettings(token)
      .then((res) => {
        const data = res?.data?.data?.data;
        updateLocalStates(data);
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [token, showSpin, hideSpin, unauthorize, updateLocalStates]);

  useEffect(() => {
    fetchAiSettings();
  }, [fetchAiSettings]);

  const handleToggleChange = useCallback(
    (id?: string, value?: boolean, aiEnablOverride?: boolean) => {
      if (!decodedToken?.is_company_owner) {
        message.error('You are not a company owner');
        return;
      }

      if (!toggle && id) return;
      showSpin();
      const prevState = [...aiControlSettings];
      let newAiControlSettings = [...aiControlSettings];
      const isMainToggle = id === undefined && value === undefined;
      // Main toggle triggered OR toggle turned off  disable ALL
      if (isMainToggle) {
        newAiControlSettings = aiControlSettings.map((section) => ({
          ...section,
          ai_types: section.ai_types.map((type) => ({
            ...type,
            is_enabled: aiEnablOverride,
          })),
        }));
        setAiControlSettings(newAiControlSettings);
      }

      // option ai Individual toggle update
      else if (id !== undefined && value !== undefined) {
        newAiControlSettings = aiControlSettings.map((section) => ({
          ...section,
          ai_types: section.ai_types.map((type) =>
            type.id === id ? { ...type, is_enabled: value } : type
          ),
        }));
        setAiControlSettings(newAiControlSettings);
      }

      if (aiEnablOverride !== undefined) {
        setToggle(aiEnablOverride);
      }

      const payload: UpdateAiSettingsPayload = {
        ai_enabl: aiEnablOverride ?? toggle,
        ai_transfer_number: aiNumber?.ai_transfer_number,
        ai_agent_number: aiNumber?.ai_agent_number,
        refresh: refreshToken,
        ai_controls: newAiControlSettings.flatMap((section) =>
          section.ai_types.map((type) => ({
            control_type: type.control_type,
            is_enabled: type.is_enabled,
          }))
        ),
      };

      UpdateAiControlsSettings(payload, token)
        .then((res) => {
          const data = res?.data?.data;
          if (data?.is_success) {
            if (data?.data?.access)
              localStorage.setItem('@token', data.data.access);
            if (data?.data?.refresh)
              localStorage.setItem('@refreshToken', data.data.refresh);
            updateLocalStates(data?.data);
            message.success('AI settings updated successfully');
          } else {
            message.error(data?.message || 'Failed to update AI settings');
            setAiControlSettings(prevState);
            if (aiEnablOverride !== undefined) setToggle(!aiEnablOverride);
          }
        })
        .catch((err) => {
          setAiControlSettings(prevState);
          if (aiEnablOverride !== undefined) setToggle(!aiEnablOverride);
          unauthorize(err);
        })
        .finally(() => hideSpin());
    },
    [
      decodedToken?.is_company_owner,
      aiControlSettings,
      aiNumber?.ai_transfer_number,
      aiNumber?.ai_agent_number,
      toggle,
      token,
      refreshToken,
      unauthorize,
      showSpin,
      hideSpin,
      updateLocalStates,
    ]
  );

  return (
    <div className='settings-content-wrapper'>
      <Flex vertical gap={4}>
        <CustomTitle
          text={title}
          fontSize={20}
          fontWeight='700'
          lineHeight='28px'
          color='var(--text-primary)'
          marginBottom={0}
        />
        <CustomTitle
          text={'Applies to all users in this organization'}
          fontSize={10}
          fontWeight='400'
          lineHeight='16px'
          color='var(--text-secondary)'
          marginTop={0}
          marginBottom={0}
        />
      </Flex>

      {toggle !== null && (
        <Flex className='ai-toggle-container'>
          <ToggleSwitch
            checked={toggle}
            onChange={(e) =>
              handleToggleChange(undefined, undefined, e.target.checked)
            }
          />
          <div className='ai-toggle-text'>
            <p className='ai-toggle-title'>
              Enable AI features for this organization
            </p>
            <p className='ai-toggle-description'>
              Enable or disable AI features across the organization. When
              disabled, all AI agent capabilities will be unavailable
            </p>
          </div>
        </Flex>
      )}

      <>
        {aiControlSettings?.map((group: AiSectionGroup, index) => (
          <React.Fragment key={index}>
            <Flex
              vertical
              className={`w-full ${!toggle && 'disabled-row'}`}
              gap={16}
            >
              <AiControlTable
                group={group}
                displaySection={group?.display_section}
                onToggleChange={handleToggleChange}
              />
            </Flex>
          </React.Fragment>
        ))}
      </>
    </div>
  );
};

export default AIControls;
